/**
 * Semantic Analyzer — semantic analysis for search queries and content matching
 */

"use strict";

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);


// Semantic Analysis Engine
export const SemanticAnalyzer = {
    // Configuration
    config: {
        enableContextAnalysis: true,
        enableEntityExtraction: true,
        enableTopicModeling: true,
        maxContextWindow: 100,
        minTopicConfidence: 0.3,
        semanticThreshold: 0.4
    },

    // Knowledge base
    synonyms: new Map(),
    concepts: new Map(),
    entities: new Map(),
    topics: new Map(),
    
    // Semantic relationships
    relationships: new Map(),
    
    // Statistics
    stats: {
        analyses: 0,
        conceptsExtracted: 0,
        entitiesFound: 0,
        topicsIdentified: 0
    },

    // Initialize semantic analyzer
    initialize() {
        this.loadKnowledgeBase();
        this.setupLanguageModels();
        return this;
    },

    /**
     * Analyze query semantically
     * @param {string} query - Query to analyze
     * @param {Object} context - Additional context
     * @returns {Object} Semantic analysis result
     */
    analyzeQuery(query, context = {}) {
        this.stats.analyses++;
        
        const analysis = {
            original: query,
            normalized: this.normalizeText(query),
            tokens: this.tokenize(query),
            concepts: [],
            entities: [],
            topics: [],
            intent: null,
            confidence: 0,
            semanticVector: null
        };

        // Extract concepts
        analysis.concepts = this.extractConcepts(analysis.tokens);
        
        // Extract entities
        analysis.entities = this.extractEntities(analysis.tokens);
        
        // Identify topics
        analysis.topics = this.identifyTopics(analysis.tokens, context);
        
        // Determine intent
        analysis.intent = this.determineIntent(analysis);
        
        // Calculate confidence
        analysis.confidence = this.calculateConfidence(analysis);
        
        // Generate semantic vector
        analysis.semanticVector = this.generateSemanticVector(analysis);
        
        return analysis;
    },

    /**
     * Analyze content semantically
     * @param {string} content - Content to analyze
     * @param {Object} metadata - Content metadata
     * @returns {Object} Content analysis
     */
    analyzeContent(content, metadata = {}) {
        const analysis = {
            content,
            metadata,
            summary: this.generateSummary(content),
            keyPhrases: this.extractKeyPhrases(content),
            topics: this.identifyTopics(this.tokenize(content)),
            entities: this.extractEntities(this.tokenize(content)),
            sentiment: this.analyzeSentiment(content),
            semanticVector: null
        };

        analysis.semanticVector = this.generateContentSemanticVector(analysis);
        
        return analysis;
    },

    /**
     * Calculate semantic similarity between query and content
     * @param {Object} queryAnalysis - Analyzed query
     * @param {Object} contentAnalysis - Analyzed content
     * @returns {number} Similarity score (0-1)
     */
    calculateSemanticSimilarity(queryAnalysis, contentAnalysis) {
        let similarity = 0;
        
        // Concept overlap
        const conceptSimilarity = this.calculateConceptSimilarity(
            queryAnalysis.concepts, 
            contentAnalysis.keyPhrases
        );
        similarity += conceptSimilarity * 0.3;
        
        // Entity matching
        const entitySimilarity = this.calculateEntitySimilarity(
            queryAnalysis.entities,
            contentAnalysis.entities
        );
        similarity += entitySimilarity * 0.2;
        
        // Topic alignment
        const topicSimilarity = this.calculateTopicSimilarity(
            queryAnalysis.topics,
            contentAnalysis.topics
        );
        similarity += topicSimilarity * 0.3;
        
        // Vector similarity (if available)
        if (queryAnalysis.semanticVector && contentAnalysis.semanticVector) {
            const vectorSimilarity = this.calculateVectorSimilarity(
                queryAnalysis.semanticVector,
                contentAnalysis.semanticVector
            );
            similarity += vectorSimilarity * 0.2;
        }
        
        return Math.min(similarity, 1);
    },

    // Extract concepts from tokens
    extractConcepts(tokens) {
        const concepts = [];
        this.stats.conceptsExtracted++;
        
        // Single word concepts
        for (const token of tokens) {
            if (this.concepts.has(token.toLowerCase())) {
                concepts.push({
                    text: token,
                    type: 'concept',
                    confidence: this.concepts.get(token.toLowerCase()),
                    category: this.getConceptCategory(token)
                });
            }
        }
        
        // Multi-word concepts (n-grams)
        for (let i = 0; i < tokens.length - 1; i++) {
            const bigram = `${tokens[i]} ${tokens[i + 1]}`.toLowerCase();
            if (this.concepts.has(bigram)) {
                concepts.push({
                    text: bigram,
                    type: 'concept',
                    confidence: this.concepts.get(bigram),
                    category: this.getConceptCategory(bigram)
                });
            }
        }
        
        return concepts;
    },

    // Extract entities from tokens
    extractEntities(tokens) {
        const entities = [];
        this.stats.entitiesFound++;
        
        // Date patterns
        const datePatterns = [
            /\d{4}[-\/]\d{1,2}[-\/]\d{1,2}/,
            /\d{1,2}[-\/]\d{1,2}[-\/]\d{4}/,
            /\b\d{4}\b/
        ];
        
        const text = tokens.join(' ');
        
        for (const pattern of datePatterns) {
            const matches = text.match(pattern);
            if (matches) {
                entities.push({
                    text: matches[0],
                    type: 'date',
                    confidence: 0.9
                });
            }
        }
        
        // Number patterns
        const numberPattern = /\b\d+(?:\.\d+)?\b/g;
        const numberMatches = text.match(numberPattern);
        if (numberMatches) {
            numberMatches.forEach(match => {
                entities.push({
                    text: match,
                    type: 'number',
                    confidence: 0.8
                });
            });
        }
        
        // Known entities from knowledge base
        for (const token of tokens) {
            if (this.entities.has(token.toLowerCase())) {
                const entityInfo = this.entities.get(token.toLowerCase());
                entities.push({
                    text: token,
                    type: entityInfo.type,
                    confidence: entityInfo.confidence,
                    metadata: entityInfo.metadata
                });
            }
        }
        
        return entities;
    },

    // Identify topics in text
    identifyTopics(tokens, context = {}) {
        const topics = [];
        this.stats.topicsIdentified++;
        
        // Calculate topic scores
        const topicScores = new Map();
        
        for (const [topic, keywords] of this.topics) {
            let score = 0;
            let matches = 0;
            
            for (const token of tokens) {
                if (keywords.includes(token.toLowerCase())) {
                    score += 1;
                    matches++;
                }
            }
            
            if (matches > 0) {
                const normalizedScore = score / keywords.length;
                if (normalizedScore >= this.config.minTopicConfidence) {
                    topicScores.set(topic, {
                        score: normalizedScore,
                        matches,
                        keywords: keywords.filter(kw => tokens.includes(kw))
                    });
                }
            }
        }
        
        // Convert to sorted array
        for (const [topic, data] of topicScores) {
            topics.push({
                name: topic,
                confidence: data.score,
                matches: data.matches,
                matchedKeywords: data.keywords
            });
        }
        
        return topics.sort((a, b) => b.confidence - a.confidence);
    },

    // Determine query intent
    determineIntent(analysis) {
        const intents = {
            search: 0,
            filter: 0,
            navigation: 0,
            information: 0,
            action: 0
        };
        
        // Intent indicators
        const indicators = {
            search: ['find', 'search', 'look', 'show', 'get', 'найти', 'поиск', 'показать'],
            filter: ['filter', 'where', 'when', 'type', 'category', 'фильтр', 'где', 'когда'],
            navigation: ['go', 'open', 'page', 'link', 'перейти', 'открыть', 'страница'],
            information: ['what', 'who', 'how', 'why', 'что', 'кто', 'как', 'почему'],
            action: ['download', 'save', 'delete', 'create', 'скачать', 'сохранить', 'создать']
        };
        
        for (const token of analysis.tokens) {
            const lowerToken = token.toLowerCase();
            for (const [intent, words] of Object.entries(indicators)) {
                if (words.includes(lowerToken)) {
                    intents[intent] += 1;
                }
            }
        }
        
        // Find dominant intent
        const maxIntent = Object.entries(intents).reduce((a, b) => 
            intents[a[0]] > intents[b[0]] ? a : b
        );
        
        return {
            primary: maxIntent[0],
            confidence: maxIntent[1] / analysis.tokens.length,
            distribution: intents
        };
    },

    // Calculate analysis confidence
    calculateConfidence(analysis) {
        let confidence = 0;
        
        // Base confidence from successful extractions
        if (analysis.concepts.length > 0) confidence += 0.3;
        if (analysis.entities.length > 0) confidence += 0.2;
        if (analysis.topics.length > 0) confidence += 0.3;
        if (analysis.intent && analysis.intent.confidence > 0.5) confidence += 0.2;
        
        return Math.min(confidence, 1);
    },

    // Generate semantic vector for query
    generateSemanticVector(analysis) {
        // Simplified semantic vector based on concepts and topics
        const vector = new Array(50).fill(0);
        
        // Encode concepts
        analysis.concepts.forEach((concept, index) => {
            const position = this.hashToVector(concept.text, 25);
            vector[position] += concept.confidence;
        });
        
        // Encode topics
        analysis.topics.forEach((topic, index) => {
            const position = 25 + this.hashToVector(topic.name, 25);
            vector[position] += topic.confidence;
        });
        
        // Normalize vector
        const magnitude = Math.sqrt(vector.reduce((sum, val) => sum + val * val, 0));
        return magnitude > 0 ? vector.map(val => val / magnitude) : vector;
    },

    // Generate semantic vector for content
    generateContentSemanticVector(analysis) {
        const vector = new Array(50).fill(0);
        
        // Encode key phrases
        analysis.keyPhrases.forEach(phrase => {
            const position = this.hashToVector(phrase.text, 25);
            vector[position] += phrase.score;
        });
        
        // Encode topics
        analysis.topics.forEach(topic => {
            const position = 25 + this.hashToVector(topic.name, 25);
            vector[position] += topic.confidence;
        });
        
        // Normalize
        const magnitude = Math.sqrt(vector.reduce((sum, val) => sum + val * val, 0));
        return magnitude > 0 ? vector.map(val => val / magnitude) : vector;
    },

    // Extract key phrases from content
    extractKeyPhrases(content) {
        const phrases = [];
        const tokens = this.tokenize(content);
        const tokenFreq = new Map();
        
        // Calculate token frequencies
        tokens.forEach(token => {
            const count = tokenFreq.get(token) || 0;
            tokenFreq.set(token, count + 1);
        });
        
        // Generate phrases (1-3 grams)
        for (let n = 1; n <= 3; n++) {
            for (let i = 0; i <= tokens.length - n; i++) {
                const phrase = tokens.slice(i, i + n).join(' ');
                const score = this.calculatePhraseScore(phrase, tokenFreq, content.length);
                
                if (score > 0.1) {
                    phrases.push({
                        text: phrase,
                        score,
                        frequency: tokenFreq.get(phrase) || 1
                    });
                }
            }
        }
        
        return phrases.sort((a, b) => b.score - a.score).slice(0, 20);
    },

    // Calculate phrase importance score
    calculatePhraseScore(phrase, tokenFreq, totalLength) {
        const tokens = phrase.split(' ');
        let score = 0;
        
        // Frequency score
        const freq = tokenFreq.get(phrase) || 1;
        score += Math.log(freq + 1) / Math.log(totalLength + 1);
        
        // Length bonus (prefer longer phrases)
        score += (tokens.length - 1) * 0.1;
        
        // Concept bonus
        if (this.concepts.has(phrase.toLowerCase())) {
            score += this.concepts.get(phrase.toLowerCase());
        }
        
        return score;
    },

    // Analyze sentiment of text
    analyzeSentiment(text) {
        // Simple sentiment analysis
        const positiveWords = ['good', 'great', 'excellent', 'amazing', 'хорошо', 'отлично'];
        const negativeWords = ['bad', 'terrible', 'awful', 'horrible', 'плохо', 'ужасно'];
        
        const tokens = this.tokenize(text.toLowerCase());
        let positive = 0;
        let negative = 0;
        
        tokens.forEach(token => {
            if (positiveWords.includes(token)) positive++;
            if (negativeWords.includes(token)) negative++;
        });
        
        const total = positive + negative;
        if (total === 0) {
            return { polarity: 'neutral', confidence: 0 };
        }
        
        const score = (positive - negative) / total;
        return {
            polarity: score > 0.1 ? 'positive' : score < -0.1 ? 'negative' : 'neutral',
            confidence: Math.abs(score),
            positive,
            negative
        };
    },

    // Calculate concept similarity
    calculateConceptSimilarity(concepts1, concepts2) {
        if (!concepts1.length || !concepts2.length) return 0;
        
        let matches = 0;
        const set2 = new Set(concepts2.map(c => c.text?.toLowerCase() || c.toLowerCase()));
        
        for (const concept of concepts1) {
            const text = concept.text?.toLowerCase() || concept.toLowerCase();
            if (set2.has(text)) {
                matches++;
            }
        }
        
        return matches / Math.max(concepts1.length, concepts2.length);
    },

    // Calculate entity similarity
    calculateEntitySimilarity(entities1, entities2) {
        if (!entities1.length || !entities2.length) return 0;
        
        let matches = 0;
        const set2 = new Set(entities2.map(e => e.text.toLowerCase()));
        
        for (const entity of entities1) {
            if (set2.has(entity.text.toLowerCase())) {
                matches++;
            }
        }
        
        return matches / Math.max(entities1.length, entities2.length);
    },

    // Calculate topic similarity
    calculateTopicSimilarity(topics1, topics2) {
        if (!topics1.length || !topics2.length) return 0;
        
        let similarity = 0;
        for (const topic1 of topics1) {
            for (const topic2 of topics2) {
                if (topic1.name === topic2.name) {
                    similarity += Math.min(topic1.confidence, topic2.confidence);
                }
            }
        }
        
        return similarity / Math.max(topics1.length, topics2.length);
    },

    // Calculate vector similarity (cosine similarity)
    calculateVectorSimilarity(vector1, vector2) {
        if (vector1.length !== vector2.length) return 0;
        
        let dotProduct = 0;
        let magnitude1 = 0;
        let magnitude2 = 0;
        
        for (let i = 0; i < vector1.length; i++) {
            dotProduct += vector1[i] * vector2[i];
            magnitude1 += vector1[i] * vector1[i];
            magnitude2 += vector2[i] * vector2[i];
        }
        
        magnitude1 = Math.sqrt(magnitude1);
        magnitude2 = Math.sqrt(magnitude2);
        
        if (magnitude1 === 0 || magnitude2 === 0) return 0;
        
        return dotProduct / (magnitude1 * magnitude2);
    },

    // Normalize text for processing \u2014 Unicode-aware (EN/RU/DE/FR/ES diacritics preserved).
    normalizeText(text) {
        return text.toLowerCase()
            .replace(/[^\p{L}\p{N}\s]/gu, ' ')
            .replace(/\s+/g, ' ')
            .trim();
    },

    // Tokenize text
    tokenize(text) { return this.normalizeText(text).split(/\s+/).filter(token => token.length > 0); },

    // Hash string to vector position
    hashToVector(str, size) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash) % size;
    },

    // Get concept category
    getConceptCategory(concept) {
        // Simple categorization
        const categories = {
            'time': ['time', 'date', 'year', 'month', 'day', 'время', 'дата', 'год'],
            'location': ['place', 'location', 'city', 'country', 'место', 'город', 'страна'],
            'person': ['person', 'author', 'name', 'человек', 'автор', 'имя'],
            'technology': ['computer', 'software', 'tech', 'компьютер', 'программа'],
            'science': ['research', 'study', 'analysis', 'исследование', 'изучение']
        };
        
        const lowerConcept = concept.toLowerCase();
        for (const [category, keywords] of Object.entries(categories)) {
            if (keywords.some(keyword => lowerConcept.includes(keyword))) {
                return category;
            }
        }
        
        return 'general';
    },

    // Load knowledge base
    loadKnowledgeBase() {
        // Load basic concepts
        this.concepts.set('документ', 0.9);
        this.concepts.set('файл', 0.9);
        this.concepts.set('поиск', 0.8);
        this.concepts.set('текст', 0.8);
        this.concepts.set('document', 0.9);
        this.concepts.set('file', 0.9);
        this.concepts.set('search', 0.8);
        this.concepts.set('text', 0.8);
        
        // Load topics
        this.topics.set('programming', ['code', 'programming', 'software', 'algorithm']);
        this.topics.set('science', ['research', 'study', 'analysis', 'experiment']);
        this.topics.set('education', ['learn', 'teach', 'course', 'lesson']);
        
    },

    // Setup language models
    setupLanguageModels() {
        // This would initialize more sophisticated NLP models
    },

    // Get analysis statistics
    getStats() {
        return {
            ...this.stats,
            knowledgeBase: {
                concepts: this.concepts.size,
                entities: this.entities.size,
                topics: this.topics.size,
                relationships: this.relationships.size
            }
        };
    },

    // Generate summary from content
    generateSummary(content, maxLength = 200) {
        if (!content) return '';
        
        if (debugMode === '1') {
                console.log('[SemanticAnalyzer] Generating summary for content length:', content.length);
            }
        
        try {
            // Simple extractive summarization
            const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 10);
            
            if (sentences.length <= 2) {
                return content.substring(0, maxLength) + (content.length > maxLength ? '...' : '');
            }
            
            // Score sentences by keyword density and position
            const keywordFreq = this._extractKeywords(content);
            const scoredSentences = sentences.map((sentence, index) => {
                let score = 0;
                
                // Position bonus (earlier sentences get higher score)
                score += (sentences.length - index) / sentences.length * 0.3;
                
                // Keyword density bonus
                const words = sentence.toLowerCase().split(/\s+/);
                words.forEach(word => {
                    if (keywordFreq[word]) {
                        score += keywordFreq[word] * 0.1;
                    }
                });
                
                // Length bonus (prefer medium-length sentences)
                const idealLength = 50;
                score += Math.max(0, 1 - Math.abs(words.length - idealLength) / idealLength) * 0.2;
                
                return { sentence: sentence.trim(), score, index };
            });
            
            // Select top sentences
            scoredSentences.sort((a, b) => b.score - a.score);
            const selectedSentences = scoredSentences
                .slice(0, Math.min(3, Math.ceil(sentences.length * 0.3)))
                .sort((a, b) => a.index - b.index)
                .map(item => item.sentence);
            
            let summary = selectedSentences.join('. ') + '.';
            
            // Trim to maxLength if needed
            if (summary.length > maxLength) {
                summary = summary.substring(0, maxLength - 3) + '...';
            }
            
            if (debugMode === '1') {
                console.log('[SemanticAnalyzer] Generated summary length:', summary.length);
            }
            return summary;
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[SemanticAnalyzer] Summary generation failed:', error);
            }
            return content.substring(0, maxLength) + (content.length > maxLength ? '...' : '');
        }
    },

    // Extract keywords with frequency
    _extractKeywords(content) {
        const words = this.tokenize(content);
        const keywordFreq = {};
        words.forEach(word => {
            keywordFreq[word] = (keywordFreq[word] || 0) + 1;
        });
        return keywordFreq;
    }
};

// Auto-initialize
SemanticAnalyzer.initialize(); 